import { useDispatch, useSelector } from "react-redux";

import {
  Box,
  ContextMenuButton,
  ContextMenuLink,
  Divider,
  Icon,
  Inline,
  Link,
  Stack,
  Text,
  TextHoverMenu,
  useBreakpoint
} from "@seneca/senekit";

import { linkToHealthResourcesArticle, linkToRegionModal } from "common/links";

import {
  getUserAccountType,
  getUserEmail,
  getUserGivenName,
  getUserNickName
} from "seneca-common/features/user/state";
import { openWindowSecurely } from "seneca-common/utils/routing/index";

import useUpdateApp from "features/app/hooks/useUpdateApp";
import { RocketEmoji } from "features/common/emoji/components/Emoji";
import Logo from "features/common/logos/Logo";
import DarkModeMenuButton from "features/dark-mode/components/DarkModeMenuButton";
import { useDarkModeGatingCriteria } from "features/dark-mode/hooks/useDarkModeGatingCriteria";
import {
  linkToDashboard,
  linkToDashboardFeedback,
  linkToDashboardInviteParentsModal,
  linkToStudentHelpSection
} from "features/dashboard/links";
import { linkToStudentClasses } from "features/dashboard/pages/classes/links";
import { linkToCoursesPage } from "features/dashboard/pages/courses-page/links";
import { linkToSettings } from "features/dashboard/pages/settings-page/links";
import {
  makeLinkToWhatIsPremium,
  SUBSCRIPTION_JOURNEY_SOURCES
} from "features/dashboard/pages/what-is-premium-page/routes/links";
import { NavbarSideDrawer } from "features/navbar";
import { useIsPlurallApp } from "features/plurall";
import {
  getSellPremiumToUser,
  getSellUpgradeToUser
} from "features/subscriptions/utils/showPremiumAds";
import {
  Stage,
  useTutoringActivationJourneyLink
} from "features/tutoring/activation-journey";
import { analyticsLogUserClickedActivateTutoringNavbar } from "features/tutoring/activation-journey/analytics";
import { useOpenTutoringActivationJourneyIfInactive } from "features/tutoring/useOpenTutoringActivationJourneyIfInactive";
import { setPlurallHelpCentreModalOpen } from "features/ui-state/actions";

import { getLinkToPlurallStore } from "../../../plurall/links";

type Props = {
  name: string;
  isTeacher: boolean;
  showRegionLink: boolean;
  hasLiveSubscription: boolean;
  signOut: () => void;
  locale: string;
  region: string;
};

export default function DashboardNavbar(props: Props) {
  const { isMobileOrTablet } = useBreakpoint();

  const sellUpgradeToUser = useSelector(getSellUpgradeToUser);
  const sellPremiumToUser = useSelector(getSellPremiumToUser);
  const email = useSelector(getUserEmail);

  const { update, ready, updating } = useUpdateApp();

  const { hasTutoringInactive } = useOpenTutoringActivationJourneyIfInactive();

  const commonProps = {
    ...props,
    email,
    sellUpgradeToUser,
    sellPremiumToUser,
    updateAppVersion: update,
    appVersionUpdateReady: ready,
    appVersionIsUpdating: updating,
    hasTutoringInactive
  };

  return (
    <Box
      marginX={["large", "xlarge"]}
      marginY="xlarge"
      dataTest="DashboardNavbar"
    >
      <Inline
        flexWrap="nowrap"
        justifyContent="space-between"
        alignItems="center"
      >
        <Link to={linkToDashboard()}>
          <Logo height={["36px", "40px"]} maxWidth="200px" />
        </Link>

        {isMobileOrTablet ? (
          <MobileNavbar {...commonProps} />
        ) : (
          <DesktopNavbar {...commonProps} />
        )}
      </Inline>
    </Box>
  );
}

function DesktopNavbar({
  sellPremiumToUser,
  sellUpgradeToUser,
  isTeacher,
  hasTutoringInactive,
  ...hoverMenuProps
}: any) {
  const isPlurallApp = useIsPlurallApp();
  const linkToWhatIsPremium = isPlurallApp
    ? getLinkToPlurallStore()
    : linkToWhatIsPremiumPage();

  const linkToTutoringActivation = useTutoringActivationJourneyLink({
    stage: Stage.Start
  });

  return (
    <Inline space="xxlarge">
      <Link exact navLink to={linkToDashboard()} tone="neutral">
        Home
      </Link>
      <Link navLink to={linkToCoursesPage()} tone="neutral">
        Courses
      </Link>
      <Link navLink to={linkToStudentClasses()} tone="neutral">
        Classes & assignments
      </Link>
      {!isTeacher && sellPremiumToUser && (
        <Link navLink to={linkToWhatIsPremium} tone="neutral">
          <GetPremium />
        </Link>
      )}
      {hasTutoringInactive && (
        <Link
          to={linkToTutoringActivation}
          onClick={analyticsLogUserClickedActivateTutoringNavbar}
          tone="neutral"
        >
          <ActivateTutoring />
        </Link>
      )}
      {!isTeacher && sellUpgradeToUser && (
        <Link navLink to={linkToWhatIsPremiumPage()} tone="neutral">
          <UpgradePremium />
        </Link>
      )}

      {/* @ts-ignore */}
      <DesktopHoverMenu {...hoverMenuProps} />
    </Inline>
  );
}

function DesktopHoverMenu({
  email,
  locale,
  signOut,
  showRegionLink,
  updateAppVersion,
  appVersionUpdateReady,
  appVersionIsUpdating
}: any) {
  const dispatch = useDispatch();
  const isPlurallApp = useIsPlurallApp();
  const givenName = useSelector(getUserGivenName);
  const nickname = useSelector(getUserNickName);
  const role = useSelector(getUserAccountType);
  const name = role === "schoolteacher" && nickname ? nickname : givenName;

  const { isLoading: isDarkModeCriteriaLoading } = useDarkModeGatingCriteria();
  return (
    <TextHoverMenu position="bottomLeft" ariaLabel="More navigation options">
      {{
        label: (
          <Text dataNoTranslate size="medium" weight="bold">
            {name}
          </Text>
        ),
        options: (
          <Box width="200px" margin="large">
            <Stack space="large">
              {!isPlurallApp && (
                <>
                  <Box margin="large">
                    <Text dataNoTranslate size="small" tone="neutral" truncate>
                      {email}
                    </Text>
                  </Box>

                  <Divider direction="horizontal" />
                </>
              )}

              <Stack>
                <ContextMenuLink to={linkToSettings()}>
                  {{
                    icon: (
                      <Icon
                        iconName="cog"
                        size="medium"
                        accessibilityLabel={null}
                        tone="neutral"
                      />
                    ),
                    text: <Text>Settings</Text>
                  }}
                </ContextMenuLink>
                {!isDarkModeCriteriaLoading && <DarkModeMenuButton />}

                {!isPlurallApp && (
                  <ContextMenuLink to={linkToDashboardInviteParentsModal()}>
                    {{
                      icon: (
                        <Icon
                          iconName="group"
                          size="medium"
                          accessibilityLabel={null}
                          tone="neutral"
                        />
                      ),
                      text: <Text>Invite parent</Text>
                    }}
                  </ContextMenuLink>
                )}

                {showRegionLink && (
                  <ContextMenuLink to={linkToRegionModal()}>
                    {{
                      icon: (
                        <Icon
                          iconName="globe"
                          size="medium"
                          accessibilityLabel={null}
                          tone="neutral"
                        />
                      ),
                      text: <Text>Region</Text>
                    }}
                  </ContextMenuLink>
                )}

                {!isPlurallApp && (
                  <ContextMenuLink to={linkToDashboardFeedback()}>
                    {{
                      icon: (
                        <Icon
                          iconName="megaphone"
                          size="medium"
                          accessibilityLabel={null}
                          tone="neutral"
                        />
                      ),
                      text: <Text>Feedback</Text>
                    }}
                  </ContextMenuLink>
                )}

                <ContextMenuButton
                  onClick={() =>
                    isPlurallApp
                      ? dispatch(setPlurallHelpCentreModalOpen())
                      : openWindowSecurely(linkToStudentHelpSection(locale))
                  }
                >
                  {{
                    icon: (
                      <Icon
                        iconName="help"
                        size="medium"
                        accessibilityLabel={"Help"}
                        tone="neutral"
                      />
                    ),
                    text: <Text>Help centre</Text>
                  }}
                </ContextMenuButton>
                {/* TODO Look into if we will replace this with SOMOS equivalent */}
                {!isPlurallApp && (
                  <ContextMenuButton
                    onClick={() =>
                      openWindowSecurely(linkToHealthResourcesArticle(locale))
                    }
                  >
                    {{
                      icon: (
                        <Icon
                          iconName="heart"
                          size="medium"
                          accessibilityLabel={null}
                          tone="neutral"
                        />
                      ),
                      text: <Text>Mental wellbeing</Text>
                    }}
                  </ContextMenuButton>
                )}
              </Stack>

              <Divider direction="horizontal" />

              {appVersionUpdateReady && [
                <Stack>
                  <ContextMenuButton onClick={updateAppVersion}>
                    {{
                      icon: (
                        <Icon
                          iconName="reassign"
                          size="medium"
                          accessibilityLabel={null}
                          tone="neutral"
                        />
                      ),
                      text: (
                        <Text>
                          {appVersionIsUpdating ? (
                            <>Updating...</>
                          ) : (
                            <>Update Seneca</>
                          )}
                        </Text>
                      )
                    }}
                  </ContextMenuButton>
                </Stack>,
                <Divider direction="horizontal" />
              ]}

              <ContextMenuButton onClick={signOut}>
                {{
                  icon: (
                    <Icon
                      iconName="leave"
                      size="medium"
                      accessibilityLabel={null}
                      tone="neutral"
                    />
                  ),
                  text: <Text>Logout</Text>
                }}
              </ContextMenuButton>
            </Stack>
          </Box>
        )
      }}
    </TextHoverMenu>
  );
}

function MobileNavbar({
  email,
  showRegionLink,
  locale,
  signOut,
  sellPremiumToUser,
  sellUpgradeToUser,
  isTeacher,
  updateAppVersion,
  appVersionUpdateReady,
  appVersionIsUpdating,
  hasTutoringInactive
}: any) {
  const isPlurallApp = useIsPlurallApp();
  const dispatch = useDispatch();

  const linkToTutoringActivation = useTutoringActivationJourneyLink({
    stage: Stage.YearGroup
  });

  return (
    <NavbarSideDrawer>
      {{
        options: (closeDrawer: any) => (
          <Stack space="large">
            {!isPlurallApp && (
              <>
                <Box margin="large">
                  <Text dataNoTranslate size="small" tone="neutral" truncate>
                    {email}
                  </Text>
                </Box>

                <Divider direction="horizontal" />
              </>
            )}

            <Stack>
              <ContextMenuLink onClick={closeDrawer} to={linkToDashboard()}>
                <Text>Home</Text>
              </ContextMenuLink>
              <ContextMenuLink onClick={closeDrawer} to={linkToCoursesPage()}>
                <Text>Courses</Text>
              </ContextMenuLink>
              <ContextMenuLink
                onClick={closeDrawer}
                to={linkToStudentClasses()}
              >
                <Text>Classes & assignments</Text>
              </ContextMenuLink>
              {!isPlurallApp && !isTeacher && sellPremiumToUser && (
                <ContextMenuLink
                  onClick={closeDrawer}
                  to={linkToWhatIsPremiumPage()}
                >
                  <Text>
                    <GetPremium />
                  </Text>
                </ContextMenuLink>
              )}
              {isPlurallApp && !isTeacher && sellPremiumToUser && (
                <ContextMenuButton
                  onClick={() => openWindowSecurely(getLinkToPlurallStore())}
                >
                  <Text>
                    <GetPremium />
                  </Text>
                </ContextMenuButton>
              )}
              {hasTutoringInactive && (
                <ContextMenuLink
                  onClick={() => {
                    analyticsLogUserClickedActivateTutoringNavbar();
                    closeDrawer();
                  }}
                  to={linkToTutoringActivation}
                >
                  <Text>
                    <ActivateTutoring />
                  </Text>
                </ContextMenuLink>
              )}
              {!isPlurallApp && !isTeacher && sellUpgradeToUser && (
                <ContextMenuLink
                  onClick={closeDrawer}
                  to={linkToWhatIsPremiumPage()}
                >
                  <Text>
                    <UpgradePremium />
                  </Text>
                </ContextMenuLink>
              )}
            </Stack>

            <Divider direction="horizontal" />

            <Stack>
              <ContextMenuLink onClick={closeDrawer} to={linkToSettings()}>
                {{
                  icon: (
                    <Icon
                      iconName="cog"
                      size="medium"
                      accessibilityLabel={null}
                      tone="neutral"
                    />
                  ),
                  text: <Text>Settings</Text>
                }}
              </ContextMenuLink>
              <DarkModeMenuButton onClick={closeDrawer} />

              {showRegionLink && (
                <ContextMenuLink onClick={closeDrawer} to={linkToRegionModal()}>
                  {{
                    icon: (
                      <Icon
                        iconName="globe"
                        size="medium"
                        accessibilityLabel={null}
                        tone="neutral"
                      />
                    ),
                    text: <Text>Region</Text>
                  }}
                </ContextMenuLink>
              )}

              {!isPlurallApp && (
                <ContextMenuLink
                  onClick={closeDrawer}
                  to={linkToDashboardFeedback()}
                >
                  {{
                    icon: (
                      <Icon
                        iconName="megaphone"
                        size="medium"
                        accessibilityLabel={null}
                        tone="neutral"
                      />
                    ),
                    text: <Text>Feedback</Text>
                  }}
                </ContextMenuLink>
              )}

              {!isPlurallApp && (
                <ContextMenuLink
                  to={linkToDashboardInviteParentsModal()}
                  onClick={closeDrawer}
                >
                  {{
                    icon: (
                      <Icon
                        iconName="group"
                        size="medium"
                        accessibilityLabel={null}
                        tone="neutral"
                      />
                    ),
                    text: <Text>Invite parent</Text>
                  }}
                </ContextMenuLink>
              )}

              <ContextMenuButton
                onClick={() =>
                  isPlurallApp
                    ? dispatch(setPlurallHelpCentreModalOpen())
                    : openWindowSecurely(linkToStudentHelpSection(locale))
                }
              >
                {{
                  icon: (
                    <Icon
                      iconName="help"
                      size="medium"
                      accessibilityLabel={null}
                      tone="neutral"
                    />
                  ),
                  text: <Text>Help centre</Text>
                }}
              </ContextMenuButton>

              {/* TODO Look into if we will replace this with SOMOS equivalent */}
              {!isPlurallApp && (
                <ContextMenuButton
                  onClick={() =>
                    openWindowSecurely(linkToHealthResourcesArticle(locale))
                  }
                >
                  {{
                    icon: (
                      <Icon
                        iconName="heart"
                        size="medium"
                        accessibilityLabel={null}
                        tone="neutral"
                      />
                    ),
                    text: <Text>Mental wellbeing</Text>
                  }}
                </ContextMenuButton>
              )}
            </Stack>

            <Divider direction="horizontal" />

            {appVersionUpdateReady && [
              <Stack>
                <ContextMenuButton onClick={updateAppVersion}>
                  {{
                    icon: (
                      <Icon
                        iconName="reassign"
                        size="medium"
                        accessibilityLabel={null}
                        tone="neutral"
                      />
                    ),
                    text: (
                      <Text>
                        {appVersionIsUpdating ? (
                          <>Updating...</>
                        ) : (
                          <>Update Seneca</>
                        )}
                      </Text>
                    )
                  }}
                </ContextMenuButton>
              </Stack>,
              <Divider direction="horizontal" />
            ]}

            <ContextMenuButton onClick={signOut}>
              {{
                icon: (
                  <Icon
                    iconName="leave"
                    size="medium"
                    accessibilityLabel={null}
                    tone="neutral"
                  />
                ),
                text: <Text>Logout</Text>
              }}
            </ContextMenuButton>
          </Stack>
        )
      }}
    </NavbarSideDrawer>
  );
}

const GetPremium = () => (
  <Inline space="medium">
    <RocketEmoji />
    Get premium
  </Inline>
);

const UpgradePremium = () => (
  <Inline space="medium">
    <RocketEmoji />
    Upgrade
  </Inline>
);

const ActivateTutoring = () => (
  <Inline space="medium">
    <RocketEmoji />
    Activate tutoring
  </Inline>
);

function linkToWhatIsPremiumPage() {
  return makeLinkToWhatIsPremium({
    source: SUBSCRIPTION_JOURNEY_SOURCES.DASHBOARD_NAVBAR
  });
}
